import axios from "axios";
import Vue from "vue";
import Vuex, { createStore } from "vuex";
import * as _ from "lodash-es";
import DocumentsStore from "@/features/documents/DocumentsStore";
import FollowupsStore from "@/dashboard/followups/FollowupsStore";
import SystemEventsStore from "@/dashboard/systemEvents/SystemEventsStore";
import DashboardStore from "@/dashboard/DashboardStore";
import StaffStore from "@/dashboard/staff/StaffStore";
import ParentStore from "@/features/parent/ParentStore";
import InterviewStore from "@/features/interview/InterviewStore";
import CustomFormStore from "@/features/custom-form/CustomFormStore";
import api from "@/api";
import { USER_KIND_STAFF, USER_KIND_VOLUNTEER, USER_KIND_PARENT } from "@/constants";
import { USER_GROUP_SUPERUSER } from "@/features/accounts/constants";
import CampusStore from "@/features/campuses/CampusStore";
import InterviewQuestionsStore from "@/features/questions/InterviewQuestionsStore";
import EmailPreferencesStore from "@/features/email_preferences/EmailPreferencesStore";
import CustomCategoriesStore from "@/features/custom-categories/CustomCategoriesStore";
import StaffEventsStore from "@/features/events/StaffEventsStore";
import LeadStore from "@/features/leads/LeadStore";
import OrderLineStore from "@/features/order-line/OrderLineStore";
import AddressBookStore from "@/features/address-book/AddressBookStore";
import FilterBookmarksStore from "@/features/filter-bookmarks/FilterBookmarksStore";
import SMSStore from "@/features/sms/SMSStore";
import DictionaryStore from "@/features/dictionary/DictionaryStore";
import HelpReferenceStore from "@/features/help-reference/HelpReferenceStore";
import NotificationsStore from "@/features/notifications/NotificationsStore";
import WebSocketStore from "@/features/ws/WebSocketStore";
import EmailTemplateTagsStore from "@/features/email-template-tags/EmailTemplateTagsStore";
import { USER_GROUP } from "@/features/accounts/groups/constants";

const storeConfig = {
  modules: {
    documents: DocumentsStore,
    followups: FollowupsStore,
    systemEvents: SystemEventsStore,
    interview: InterviewStore,
    campus: CampusStore,
    dashboard: DashboardStore,
    customForm: CustomFormStore,
    staffStore: StaffStore,
    staffEventsStore: StaffEventsStore,
    parentStore: ParentStore,
    interviewQuestions: InterviewQuestionsStore,
    emailPreferences: EmailPreferencesStore,
    customCategories: CustomCategoriesStore,
    lead: LeadStore,
    orderLine: OrderLineStore,
    addressBook: AddressBookStore,
    filterBookmarks: FilterBookmarksStore,
    sms: SMSStore,
    dictionary: DictionaryStore,
    helpReference: HelpReferenceStore,
    notification: NotificationsStore,
    webSocket: WebSocketStore,
    emailTemplateTags: EmailTemplateTagsStore,
  },
  state: {
    user: null,
    isPrint: false,
    embedStartUrl: null,
    UIKind: null,
  },
  mutations: {
    patchUser: function (state, patch) {
      state.user = { ...state.user, ...patch };
    },
    setUser: function (state, user) {
      state.user = user;
    },
    clearUser: function (state) {
      state.user = null;
    },
    setUIKind: function (state, kind) {
      state.UIKind = kind;
    },
    setIsPrint: function (state) {
      state.isPrint = true;
    },
    setEmbedStartUrl: function (state, url) {
      state.embedStartUrl = url;
    },
  },
  getters: {
    isEmbed: (state) => Boolean(state.embedStartUrl),
    isAuthenticated: (state) => Boolean(state.user),
    isStaff: (state) => state.user && state.user.kind === USER_KIND_STAFF,
    isSuperuser: (state, getters) =>
      getters.isStaff && getters.userGroups.includes(USER_GROUP_SUPERUSER),
    userGroups: (state) => (state.user && state.user.groups) || [],
    isParent: (state) => state.user && state.user.kind === USER_KIND_PARENT,
    isVolunteer: (state) => state.user && state.user.kind === USER_KIND_VOLUNTEER,
    hasAnyGroup: (state, getters) => (groups) => {
      if (!_.isArray(groups)) {
        throw new Error(`Array of strings is required, got "${groups}".`);
      }
      if (!groups.length) {
        throw new Error("Groups list may not be empty.");
      }
      return getters.isSuperuser || _.intersection(groups, getters.userGroups).length > 0;
    },
    hasEditApplicationPermission: (state, getters) =>
      getters.hasAnyGroup([USER_GROUP.EDIT_STUDENT_PROFILE]),
  },
  actions: {
    logout() {
      return api.logout();
    },
    login({ dispatch }, { user_data, ...tokens }) {
      api.setToken(tokens);
      dispatch("initUser", user_data);
    },
    async fetchUserData({ dispatch }) {
      const userData = await axios.get("/user-data/").then((r) => r.data);
      dispatch("initUser", userData);
      return userData;
    },
    initUser({ commit, state }, data) {
      const { user } = data;
      if (!user) {
        api.clearToken();
        return;
      }

      if (window.sentryConfigureScope) {
        window.sentryConfigureScope(function (scope) {
          scope.setUser({ email: user.email });
        });
      }

      // Special case for staff, who can be a volunteer.
      const isStaffVolunteer =
        state.UIKind === USER_KIND_VOLUNTEER && user.kind === USER_KIND_STAFF;
      if (state.UIKind && state.UIKind !== user.kind && !isStaffVolunteer) {
        // handle case when admin logins into user app and vice versa
        if (user.kind === USER_KIND_STAFF) {
          window.location = "/dashboard/";
        } else if (user.kind === USER_KIND_VOLUNTEER) {
          window.location = "/volunteer/dashboard";
        } else {
          window.location = "/";
        }
      } else {
        commit("setUser", user);
        if (user.kind === USER_KIND_STAFF) {
          commit("staffStore/setStaffData", data);
        } else if (user.kind === USER_KIND_PARENT) {
          commit("parentStore/setParentData", user);
        }
      }
    },
  },
};

const buildStore = () => {
  if (process.env.VUE_APP_COMPAT) {
    return createStore(storeConfig);
  } else {
    Vue.use(Vuex);
    return new Vuex.Store(storeConfig);
  }
};

const store = buildStore();

export function setupStore(app, kind) {
  store.commit("setUIKind", kind);
  if (process.env.VUE_APP_COMPAT) {
    app.use(store);
  }
}

export default store;
